import React, { useState, useEffect } from "react";
import {
  Container,
  Box,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Slider,
  Chip,
  Card,
  CardContent,
  Fade,
} from "@mui/material";
import {
  CreditCard,
  Percent,
  Calendar,
  Calculator,
  Wallet,
  ChevronRight,
  HelpCircle,
  ArrowRight,
  XCircle,
  TrendingUp,
  ShieldCheck,
} from "lucide-react";
import analyticsImg from "./analytics.png";
import ResponsiveAppBar from "./AppBar";
import Footer from "./Footer";
import { useNavigate } from "react-router-dom";

const EMICalculator = () => {
  const [loanAmount, setLoanAmount] = useState(100000);
  const [interestRate, setInterestRate] = useState(14);
  const [loanTerm, setLoanTerm] = useState(12);
  const [emiBreakdownOpen, setEmiBreakdownOpen] = useState(false);
  const [activeResultTab, setActiveResultTab] = useState("monthly");
  const [animateResults, setAnimateResults] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setAnimateResults(false);
    const timer = setTimeout(() => setAnimateResults(true), 100);
    return () => clearTimeout(timer);
  }, [loanAmount, interestRate, loanTerm]);

  // EMI Calculation Formula
  const calculateEMI = (principal, rate, months) => {
    if (!principal || !rate || !months) return 0;
    const monthlyRate = rate / 100 / 12;
    const emi =
      (principal * monthlyRate * Math.pow(1 + monthlyRate, months)) /
      (Math.pow(1 + monthlyRate, months) - 1);
    return isNaN(emi) ? 0 : emi;
  };

  const rawEmi = calculateEMI(loanAmount, interestRate, loanTerm);
  const emi = rawEmi.toFixed(2);
  const totalAmount = (rawEmi * loanTerm).toFixed(2);
  const totalInterest = Math.max(0, totalAmount - loanAmount).toFixed(2);

  const getEMIBreakdown = () => {
    let balance = loanAmount;
    const monthlyRate = interestRate / 100 / 12;
    const breakdown = [];

    for (let i = 1; i <= loanTerm; i++) {
      const interestPaid = balance * monthlyRate;
      const principalPaid = rawEmi - interestPaid;
      balance -= principalPaid;

      breakdown.push({
        month: i,
        emi: rawEmi.toFixed(2),
        principalPaid: principalPaid.toFixed(2),
        interestPaid: interestPaid.toFixed(2),
        balance: balance > 0 ? balance.toFixed(2) : "0.00",
      });
    }

    return breakdown;
  };

  const handleSliderChange = (setter) => (event, newValue) => {
    setter(newValue);
  };

  const handleInputChange = (setter, min, max) => (event) => {
    const value = event.target.value === "" ? "" : Number(event.target.value);
    if (value === "") {
      setter("");
    } else {
      setter(Math.max(min, Math.min(max, value)));
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(Number(value) || 0);
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] text-slate-900 flex flex-col justify-between">
      <div>
        <ResponsiveAppBar />

        {/* Hero Header */}
        <section className="bg-gradient-to-b from-white via-[#f8fafc] to-[#eff6ff] text-slate-900 py-14 px-6 text-center relative overflow-hidden border-b border-slate-200/60">
          <div className="container mx-auto max-w-3xl relative z-10">
            <span className="inline-block px-4 py-1.5 rounded-full bg-teal-50 text-[#0d9488] text-xs font-bold uppercase tracking-wider mb-3 border border-teal-200/60">
              Financial Tool
            </span>
            <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight mb-3 text-slate-900">
              Loan EMI Calculator
            </h1>
            <p className="text-slate-600 text-sm sm:text-base max-w-xl mx-auto leading-relaxed">
              Plan your finances with precision. Estimate monthly installments, total interest, and view a complete month-by-month repayment schedule.
            </p>
          </div>
        </section>

        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
          <Grid container spacing={4}>
            {/* Left side: Calculator Controls */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: "24px",
                  border: "1px solid rgba(226, 232, 240, 0.9)",
                  boxShadow: "0 10px 40px rgba(0,0,0,0.05)",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    background: "linear-gradient(135deg, #936097 0%, #77c8c7 100%)",
                    color: "white",
                    py: 3,
                    px: { xs: 3, sm: 4 },
                    borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
                  }}
                >
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                    }}
                  >
                    <Calculator size={22} style={{ marginRight: "10px", color: "#77c8c7" }} />
                    EMI Estimator Parameters
                  </Typography>
                </Box>

                <Box sx={{ p: { xs: 3, sm: 4 } }}>
                  {/* Loan Amount */}
                  <Box sx={{ mb: 4 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#475569" }}>
                        Loan Amount
                      </Typography>
                      <Chip
                        label={formatCurrency(loanAmount)}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          backgroundColor: "rgba(119, 200, 199, 0.15)",
                          color: "#0f766e",
                          border: "1px solid rgba(119, 200, 199, 0.4)",
                        }}
                      />
                    </Box>
                    <Slider
                      value={typeof loanAmount === "number" ? loanAmount : 0}
                      onChange={handleSliderChange(setLoanAmount)}
                      min={10000}
                      max={5000000}
                      step={10000}
                      sx={{
                        color: "#77c8c7",
                        mb: 2,
                        "& .MuiSlider-thumb": {
                          boxShadow: "0 0 10px rgba(119, 200, 199, 0.6)",
                        },
                      }}
                    />
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={loanAmount}
                      onChange={handleInputChange(setLoanAmount, 1000, 5000000)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CreditCard size={18} color="#64748b" />
                          </InputAdornment>
                        ),
                      }}
                      size="small"
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                    />
                  </Box>

                  {/* Interest Rate */}
                  <Box sx={{ mb: 4 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#475569" }}>
                        Interest Rate (% p.a.)
                      </Typography>
                      <Chip
                        label={`${interestRate}%`}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          backgroundColor: "rgba(147, 96, 151, 0.15)",
                          color: "#936097",
                          border: "1px solid rgba(147, 96, 151, 0.4)",
                        }}
                      />
                    </Box>
                    <Slider
                      value={typeof interestRate === "number" ? interestRate : 0}
                      onChange={handleSliderChange(setInterestRate)}
                      min={1}
                      max={30}
                      step={0.5}
                      sx={{
                        color: "#936097",
                        mb: 2,
                        "& .MuiSlider-thumb": {
                          boxShadow: "0 0 10px rgba(147, 96, 151, 0.6)",
                        },
                      }}
                    />
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={interestRate}
                      onChange={handleInputChange(setInterestRate, 0.5, 30)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Percent size={18} color="#64748b" />
                          </InputAdornment>
                        ),
                      }}
                      size="small"
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                    />
                  </Box>

                  {/* Loan Term */}
                  <Box sx={{ mb: 4 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#475569" }}>
                        Loan Term (Months)
                      </Typography>
                      <Chip
                        label={`${loanTerm} Months`}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          backgroundColor: "rgba(119, 200, 199, 0.15)",
                          color: "#0f766e",
                          border: "1px solid rgba(119, 200, 199, 0.4)",
                        }}
                      />
                    </Box>
                    <Slider
                      value={typeof loanTerm === "number" ? loanTerm : 0}
                      onChange={handleSliderChange(setLoanTerm)}
                      min={3}
                      max={120}
                      step={3}
                      sx={{
                        color: "#77c8c7",
                        mb: 2,
                        "& .MuiSlider-thumb": {
                          boxShadow: "0 0 10px rgba(119, 200, 199, 0.6)",
                        },
                      }}
                    />
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={loanTerm}
                      onChange={handleInputChange(setLoanTerm, 1, 120)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Calendar size={18} color="#64748b" />
                          </InputAdornment>
                        ),
                      }}
                      size="small"
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                    />
                  </Box>

                  {/* Result Tabs */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      borderBottom: "1px solid rgba(0,0,0,0.08)",
                      mb: 3,
                    }}
                  >
                    {["monthly", "total", "breakdown"].map((tab) => (
                      <Button
                        key={tab}
                        onClick={() => {
                          if (tab === "breakdown") {
                            setEmiBreakdownOpen(true);
                          } else {
                            setActiveResultTab(tab);
                          }
                        }}
                        sx={{
                          textTransform: "none",
                          fontFamily: "'Plus Jakarta Sans', sans-serif",
                          px: 2,
                          py: 1,
                          fontWeight: activeResultTab === tab ? 700 : 500,
                          color: activeResultTab === tab ? "#936097" : "#64748b",
                          borderBottom: activeResultTab === tab ? "3px solid #936097" : "none",
                          borderRadius: 0,
                        }}
                      >
                        {tab === "monthly"
                          ? "Monthly EMI"
                          : tab === "total"
                          ? "Total Summary"
                          : "Amortization Table"}
                      </Button>
                    ))}
                  </Box>

                  {/* Results */}
                  <Fade in={animateResults} timeout={500}>
                    <Box>
                      {activeResultTab === "monthly" && (
                        <Box
                          sx={{
                            textAlign: "center",
                            py: 3.5,
                            px: 2,
                            borderRadius: "16px",
                            backgroundColor: "rgba(119, 200, 199, 0.08)",
                            border: "1px solid rgba(119, 200, 199, 0.25)",
                          }}
                        >
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Calculated Monthly EMI
                          </Typography>
                          <Typography
                            variant="h3"
                            fontWeight="bold"
                            sx={{
                              background: "linear-gradient(135deg, #936097 0%, #77c8c7 100%)",
                              WebkitBackgroundClip: "text",
                              WebkitTextFillColor: "transparent",
                              fontFamily: "'Plus Jakarta Sans', sans-serif",
                            }}
                          >
                            {formatCurrency(emi)}
                          </Typography>

                          <Box sx={{ display: "flex", gap: 2, justifyContent: "center", mt: 2.5 }}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => setEmiBreakdownOpen(true)}
                              endIcon={<ChevronRight size={16} />}
                              sx={{
                                borderRadius: "8px",
                                textTransform: "none",
                                borderColor: "#936097",
                                color: "#936097",
                                fontWeight: 600,
                              }}
                            >
                              View Payment Schedule
                            </Button>
                          </Box>
                        </Box>
                      )}

                      {activeResultTab === "total" && (
                        <Box sx={{ py: 1 }}>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                              <Card variant="outlined" sx={{ borderRadius: "12px" }}>
                                <CardContent sx={{ textAlign: "center", p: 2 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Principal
                                  </Typography>
                                  <Typography variant="body1" fontWeight="bold" sx={{ mt: 0.5 }}>
                                    {formatCurrency(loanAmount)}
                                  </Typography>
                                </CardContent>
                              </Card>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Card
                                variant="outlined"
                                sx={{
                                  borderRadius: "12px",
                                  backgroundColor: "rgba(147, 96, 151, 0.08)",
                                  borderColor: "rgba(147, 96, 151, 0.3)",
                                }}
                              >
                                <CardContent sx={{ textAlign: "center", p: 2 }}>
                                  <Typography variant="caption" sx={{ color: "#936097" }}>
                                    Total Interest
                                  </Typography>
                                  <Typography variant="body1" fontWeight="bold" sx={{ color: "#936097", mt: 0.5 }}>
                                    {formatCurrency(totalInterest)}
                                  </Typography>
                                </CardContent>
                              </Card>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Card
                                variant="outlined"
                                sx={{
                                  borderRadius: "12px",
                                  backgroundColor: "rgba(119, 200, 199, 0.08)",
                                  borderColor: "rgba(119, 200, 199, 0.3)",
                                }}
                              >
                                <CardContent sx={{ textAlign: "center", p: 2 }}>
                                  <Typography variant="caption" sx={{ color: "#0f766e" }}>
                                    Total Payable
                                  </Typography>
                                  <Typography variant="body1" fontWeight="bold" sx={{ color: "#0f766e", mt: 0.5 }}>
                                    {formatCurrency(totalAmount)}
                                  </Typography>
                                </CardContent>
                              </Card>
                            </Grid>
                          </Grid>
                        </Box>
                      )}
                    </Box>
                  </Fade>
                </Box>
              </Paper>
            </Grid>

            {/* Right side: Benefits & Action */}
            <Grid item xs={12} md={6}>
              <Box sx={{ height: "100%", display: "flex", flexDirection: "column", gap: 3.5 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    borderRadius: "24px",
                    background: "linear-gradient(135deg, #936097 0%, #77c8c7 100%)",
                    color: "white",
                    boxShadow: "0 10px 30px rgba(147, 96, 151, 0.2)",
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      fontFamily: "'Playfair Display', serif",
                      fontWeight: 700,
                      mb: 2,
                    }}
                  >
                    Smart Loan Insights
                  </Typography>
                  <Typography variant="body2" sx={{ color: "rgba(226, 232, 240, 0.8)", mb: 3, lineHeight: 1.7 }}>
                    Did you know? Choosing a shorter tenure reduces your total interest outflow significantly, while longer tenures provide smaller, easy monthly payments.
                  </Typography>

                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <TrendingUp className="text-[#77c8c7] w-5 h-5" />
                      <Typography variant="body2">Transparent interest calculated on reducing balance</Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <ShieldCheck className="text-[#936097] w-5 h-5" />
                      <Typography variant="body2">Zero foreclosure penalties on early loan closure</Typography>
                    </Box>
                  </Box>
                </Paper>

                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 3, md: 3.5 },
                    borderRadius: "24px",
                    border: "1px solid rgba(226, 232, 240, 0.9)",
                    backgroundColor: "#ffffff",
                    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.04)",
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 1.5, color: "#0f172a" }}>
                      Ready to Proceed with Your Loan?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.7 }}>
                      Apply online in just a few minutes or speak directly with our loan verification specialists to finalize your amount.
                    </Typography>

                    {/* Analytics Image Showcase */}
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        p: 2,
                        mb: 3,
                        borderRadius: "16px",
                        background: "linear-gradient(135deg, rgba(119, 200, 199, 0.08) 0%, rgba(147, 96, 151, 0.08) 100%)",
                        border: "1px solid rgba(119, 200, 199, 0.2)",
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        component="img"
                        src={analyticsImg}
                        alt="Loan Analytics"
                        sx={{
                          maxHeight: { xs: 150, sm: 180 },
                          width: "auto",
                          maxWidth: "100%",
                          objectFit: "contain",
                          filter: "drop-shadow(0 6px 16px rgba(0, 0, 0, 0.08))",
                          transition: "transform 0.3s ease",
                          "&:hover": {
                            transform: "scale(1.03)",
                          },
                        }}
                      />
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => navigate("/loan-selection")}
                      endIcon={<ArrowRight size={18} />}
                      sx={{
                        py: 1.5,
                        borderRadius: "12px",
                        background: "linear-gradient(135deg, #936097 0%, #77c8c7 100%)",
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: "0.95rem",
                        boxShadow: "0 6px 20px rgba(119, 200, 199, 0.35)",
                        "&:hover": {
                          background: "linear-gradient(135deg, #824f86 0%, #66b7b6 100%)",
                        },
                      }}
                    >
                      Apply With These Values
                    </Button>

                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => navigate("/contact")}
                      sx={{
                        py: 1.4,
                        borderRadius: "12px",
                        borderColor: "rgba(147, 96, 151, 0.4)",
                        color: "#936097",
                        textTransform: "none",
                        fontWeight: 600,
                        "&:hover": {
                          borderColor: "#936097",
                          bgcolor: "rgba(147, 96, 151, 0.04)",
                        },
                      }}
                    >
                      Talk to a Loan Specialist
                    </Button>
                  </Box>
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </Container>

        {/* Amortization Breakdown Dialog */}
        <Dialog
          open={emiBreakdownOpen}
          onClose={() => setEmiBreakdownOpen(false)}
          fullWidth
          maxWidth="md"
          PaperProps={{
            sx: {
              borderRadius: "20px",
              overflow: "hidden",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              bgcolor: "#936097",
              color: "white",
              px: 3,
              py: 2.5,
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              Amortization Payment Schedule
            </Typography>
            <IconButton onClick={() => setEmiBreakdownOpen(false)} sx={{ color: "white" }}>
              <XCircle size={22} />
            </IconButton>
          </Box>
          <DialogContent sx={{ p: 3 }}>
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{
                maxHeight: 420,
                borderRadius: "12px",
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f1f5f9" }}>
                    {["Month", "EMI", "Principal Paid", "Interest Paid", "Remaining Balance"].map((head) => (
                      <TableCell
                        key={head}
                        align="right"
                        sx={{
                          fontWeight: "bold",
                          color: "#1e293b",
                          py: 1.5,
                          backgroundColor: "#f1f5f9",
                        }}
                      >
                        {head}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getEMIBreakdown().map(({ month, emi, principalPaid, interestPaid, balance }) => (
                    <TableRow
                      key={month}
                      hover
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                        backgroundColor: month % 2 === 0 ? "#f8fafc" : "white",
                      }}
                    >
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{month}</TableCell>
                      <TableCell align="right">{formatCurrency(emi)}</TableCell>
                      <TableCell align="right">{formatCurrency(principalPaid)}</TableCell>
                      <TableCell align="right" sx={{ color: "#936097", fontWeight: 500 }}>{formatCurrency(interestPaid)}</TableCell>
                      <TableCell align="right">{formatCurrency(balance)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid rgba(0,0,0,0.06)" }}>
            <Button
              onClick={() => setEmiBreakdownOpen(false)}
              variant="contained"
              sx={{
                background: "linear-gradient(135deg, #936097 0%, #77c8c7 100%)",
                textTransform: "none",
                borderRadius: "8px",
                fontWeight: 600,
              }}
            >
              Done
            </Button>
          </DialogActions>
        </Dialog>
      </div>

      <Footer />
    </div>
  );
};

export default EMICalculator;